import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Button from '../../Components/Button/Button';
import { postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';

const OTPVerification = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);

  useEffect(() => {
    // Get email and role from location state or localStorage
    const emailFromState = location.state?.email;
    const roleFromState = location.state?.role;
    const emailFromStorage = localStorage.getItem('signupEmail');
    const roleFromStorage = localStorage.getItem('signupRole');
    
    if (emailFromState) {
      setEmail(emailFromState);
      localStorage.setItem('signupEmail', emailFromState);
      if (roleFromState) {
        setRole(roleFromState);
        localStorage.setItem('signupRole', roleFromState);
      }
    } else if (emailFromStorage) {
      setEmail(emailFromStorage);
      if (roleFromStorage) {
        setRole(roleFromStorage);
      }
    } else {
      // If no email found, redirect to signup
      navigate('/signup');
    }

    // Add animation classes on mount
    const infoSection = document.querySelector('.login-info');
    const formSection = document.querySelector('.login-form-section');
    const formCard = document.querySelector('.login-form-card');
    
    if (infoSection) infoSection.classList.add('animate-fade-in-left');
    if (formSection) formSection.classList.add('animate-fade-in-right');
    if (formCard) formCard.classList.add('animate-scale-in');

    // Focus first input on mount
    inputRefs.current?.[0]?.focus?.();
  }, [location, navigate]);

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Clear error for this field
    if (errors.otp) {
      setErrors((prev) => ({ ...prev, otp: '' }));
    }

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current?.[index + 1]?.focus?.();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current?.[index - 1]?.focus?.();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      
      // Focus last input
      inputRefs.current?.[5]?.focus?.();
      
      // Clear errors
      if (errors.otp) {
        setErrors((prev) => ({ ...prev, otp: '' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const otpString = otp.join('');
    
    // Validate OTP
    if (otpString.length !== 6) {
      setErrors({ otp: 'Please enter the complete 6-digit code' });
      inputRefs.current?.[0]?.focus?.();
      return;
    }

    if (!/^\d{6}$/.test(otpString)) {
      setErrors({ otp: 'Please enter a valid 6-digit code' });
      inputRefs.current?.[0]?.focus?.();
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // TODO: Replace with actual OTP verification API endpoint
      const response = await postApi('/auth/verify-otp', {
        email: email,
        otp: otpString,
      });

      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Email verified successfully');

        const userData = response?.data || response;
        const currentRole = role || localStorage.getItem('signupRole') || userData?.role || userData?.user_details?.role;
        const redirect = location.state?.redirect;

        // Store the entire response in localStorage
        localStorage.setItem('loginResponse', JSON.stringify(response));

        // Store user data and token if present
        if (userData) {
          localStorage.setItem('userData', JSON.stringify(userData));
          
          // Extract token from user_details and store it in localStorage
          const token = userData?.user_details?.token || userData?.token;
          if (token) {
            localStorage.setItem('token', token);
          }
          
          if (userData?.role || userData?.user_details?.role) {
            const roleToStore = userData?.user_details?.role || userData?.role;
            localStorage.setItem('role', String(roleToStore));
          }
        }

        if (currentRole) {
          setRole(currentRole);
        }

        // Clear any temporary signup data
        localStorage.removeItem('signupEmail');
        localStorage.removeItem('signupRole');

        const isTenant = currentRole === 'tenant' || currentRole === '2' || currentRole === 2;
        if (isTenant) {
          navigate('/tenant/upload-documents', {
            replace: true,
            state: redirect ? { redirect } : undefined,
          });
        } else {
          navigate('/complete-payment', { state: { email, role: currentRole } });
        }
      } else {
        setErrors({ otp: response?.message || 'Invalid verification code' });
        // Clear OTP and focus first input
        setOtp(['', '', '', '', '', '']);
        inputRefs.current?.[0]?.focus?.();
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Verification failed. Please try again.';
      setErrors({ otp: errorMessage });
      // Clear OTP and focus first input
      setOtp(['', '', '', '', '', '']);
      inputRefs.current?.[0]?.focus?.();
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      toast.error('Email not found. Please sign up again.');
      navigate('/signup');
      return;
    }

    setIsLoading(true);
    try {
      // TODO: Replace with actual resend OTP API endpoint
      const response = await postApi('/auth/resend-otp', {
        email: email,
      });

      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Verification code sent successfully');
        // Clear OTP inputs
        setOtp(['', '', '', '', '', '']);
        inputRefs.current?.[0]?.focus?.();
      } else {
        toast.error(response?.message || 'Failed to resend code');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to resend code. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-info">
          <div className="login-logo">
            <Link to="/" onClick={() => localStorage.clear()}>
              <img src={window.getPublicUrl("/assets/images/Logo.png")} alt="ClearRent" className="login-logo-img" />
            </Link>
          </div>
          <h1 className="login-welcome">Welcome back</h1>
          <p className="login-description">
            Access your dashboard to manage properties and review tenant screenings.
          </p>
        </div>
        <div className="login-form-section">
          <h2 className="login-form-title">Verify Your Email</h2>
          <p className="login-form-subtitle">
            Please enter the 6 digit code sent to {email || 'your email'}
          </p>
          <form onSubmit={handleSubmit} className={`login-form-card ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`} noValidate>
            <div className="login-form-header">Account Information</div>
            <div className={`login-form-fields ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`}>
              <div className="otp-input-container">
                <div className="otp-inputs">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { inputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      className={`otp-input ${errors.otp ? 'otp-input-error' : ''}`}
                      required
                    />
                  ))}
                </div>
                {errors.otp && (
                  <div className="error-message-container has-error">
                    <span className="error-message">{errors.otp}</span>
                  </div>
                )}
              </div>
            </div>
            <Button 
              type="submit" 
              variant="success" 
              className="login-submit-button"
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
            <p className="login-signup-link">
              Didn't receive the code?{' '}
              <button
                type="button"
                onClick={handleResendCode}
                className="resend-code-link"
                disabled={isLoading}
              >
                Resend Code
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
